import React, { useState, useRef, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { User, ThumbsUp, ThumbsDown, ArrowUp, Loader } from 'lucide-react';
import { posthog } from 'posthog-js';
import { toast } from 'sonner';
import { RootState } from '../../../reducers';
import {
  sendChatMessageWithActionsStreaming,
  handleButtonAction,
  getChatHistory,
  getChatSession,
  sendChatFeedback,
  parseActionButtons,
  cleanResponseText,
} from '../../../services/chatService';
import type {
  ChatMessage,
  ChatContext,
  ActionButton,
} from '../../../services/chatService';
import {
  selectIsPreloaded,
  selectPreloadedData,
  clearPreloadedData,
} from './chatPreloadSlice';
import DynamicActionButton from './DynamicActionButton';
import botIcon from '../../../assets/icons/new_logo.svg';
import { Button } from '../../../components/ui/button';
import { Alert, AlertDescription } from '../../../components/ui/alert';
import { Card } from '../../../components/ui/card';
import { Textarea } from '../../../components/ui/textarea';
import { cn } from '../../../lib/utils';

interface AIAssistantFormProps {
  formSource?: string;
  onClose?: () => void;
  onShowBackButton?: (show: boolean) => void;
  resetTrigger?: number;
}

// Overview component similar to Gemini chatbot - memoized to prevent re-renders
const Overview = React.memo(() => (
  <div className="text-center">
    <h2 className="text-lg font-medium text-muted-foreground mt-8">
      How can I help you today?
    </h2>
  </div>
));

const AIAssistantForm: React.FC<AIAssistantFormProps> = ({
  onShowBackButton,
  resetTrigger,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user } = useSelector((state: RootState) => state.settings);
  const { subscription, usage } = useSelector((state: RootState) => state.plan);
  const { selectedTeam } = useSelector((state: RootState) => state.team);
  const isPreloaded = useSelector(selectIsPreloaded);
  const preloadedData = useSelector(selectPreloadedData);

  // Enhanced quick action chips with more engaging design
  const quickActionChips = [
    {
      title: 'Help with recording',
      label: 'Trouble with recording or uploading videos.',
      icon: '🎥',
      color: '#F59E0B',
      predefinedQuestion:
        "I'm having trouble recording or uploading a video. Can you help me diagnose the problem?",
    },
    {
      title: 'Improve my transcript',
      label: 'Fix errors or language in your transcripts.',
      icon: '🎯',
      color: '#3B82F6',
      predefinedQuestion:
        "I'm having issues with my transcript. It might be inaccurate, incomplete, or in the wrong language. Can you help me improve it?",
    },
    {
      title: 'Account and Billing',
      label: 'Manage your subscription, usage, or billing.',
      icon: '💳',
      color: '#10B981',
      predefinedQuestion:
        'I need help with my account or billing. I might want to change my plan, check my usage, or ask about a payment.',
    },
    {
      title: 'Discover AI Features',
      label: 'Learn about AI summaries, translation & more.',
      icon: '🤖',
      color: '#8B5CF6',
      predefinedQuestion:
        "I'm interested in the AI features. What can I do with them and how do I get the best results from summaries or other tools?",
    },
    {
      title: 'Talk to sales',
      label: 'Contact with our sales team for pricing',
      icon: '💼',
      color: '#F97316',
      predefinedQuestion:
        'I would like to speak with someone from your sales team about pricing and enterprise options.',
    },
    {
      title: 'Mobile and desktop app',
      label: 'Tell me more about our mobile and desktop app.',
      icon: '📱',
      color: '#06B6D4',
      predefinedQuestion:
        'Can you tell me more about the ScreenApp mobile and desktop applications? How do I download and use them?',
    },
  ];

  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [chatInput, setChatInput] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(true);
  const [isActionButtonLoading, setIsActionButtonLoading] = useState<
    string | null
  >(null);
  const [chatError, setChatError] = useState<string | null>(null);
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);
  const [messageFeedback, setMessageFeedback] = useState<
    Record<string, { type: 'like' | 'dislike'; loading: boolean }>
  >({});

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Generate unique ID for messages
  const generateMessageId = () => {
    return `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  };

  // Process messages loaded from history to handle action buttons
  const processHistoryMessages = (messages: ChatMessage[]): ChatMessage[] => {
    return messages.map((message) => {
      if (message.role === 'assistant') {
        // If the message doesn't have action buttons but has button text in content
        if (!message.actionButtons || message.actionButtons.length === 0) {
          const buttons = parseActionButtons(message.content);
          if (buttons.length > 0) {
            return {
              ...message,
              actionButtons: buttons,
              content: cleanResponseText(message.content),
              id: message.id || generateMessageId(),
            };
          }
        } else {
          // Clean any remaining button text from content if action buttons exist
          return {
            ...message,
            content: cleanResponseText(message.content),
            id: message.id || generateMessageId(),
          };
        }
      }

      return {
        ...message,
        id: message.id || generateMessageId(),
      };
    });
  };

  // Load previous chat on mount - use preloaded data if available
  useEffect(() => {
    const loadPreviousChat = async () => {
      // First, check if we have preloaded data
      if (isPreloaded && preloadedData.messages.length > 0) {
        setChatMessages(preloadedData.messages);
        setCurrentSessionId(preloadedData.sessionId);
        // Clear preloaded data after using it to free memory
        dispatch(clearPreloadedData());
        setIsLoadingHistory(false);
        return;
      }

      // Fallback to loading from API if no preloaded data
      try {
        const historyResponse = await getChatHistory(1, 1);
        if (
          historyResponse.success &&
          historyResponse.data &&
          historyResponse.data.sessions &&
          historyResponse.data.sessions.length > 0
        ) {
          const lastSession = historyResponse.data.sessions[0];

          // Skip escalated sessions - don't load them
          if (lastSession.status === 'escalated') {
            console.log(
              'Last session was escalated, starting fresh chat instead',
            );
            setIsLoadingHistory(false);
            return;
          }

          const sessionResponse = await getChatSession(lastSession.sessionId);

          if (
            sessionResponse.success &&
            sessionResponse.data &&
            sessionResponse.data.messages &&
            sessionResponse.data.messages.length > 0 &&
            sessionResponse.data.status !== 'escalated' // Double-check escalation status
          ) {
            const processedMessages = processHistoryMessages(
              sessionResponse.data.messages,
            );
            setChatMessages(processedMessages);
            setCurrentSessionId(sessionResponse.data.sessionId);
          }
        }
      } catch (error) {
        console.log('No previous chat history found or error loading:', error);
        // Silently fail - user will start with fresh chat
      } finally {
        setIsLoadingHistory(false);
      }
    };

    loadPreviousChat();
  }, [isPreloaded, preloadedData, dispatch]);

  const handleNewChat = () => {
    setChatMessages([]);
    setCurrentSessionId(null);
    setChatInput('');
    setChatError(null);
  };

  // Notify parent when chat messages change
  useEffect(() => {
    onShowBackButton?.(chatMessages.length > 0);
  }, [chatMessages.length, onShowBackButton]);

  // Reset chat when triggered from parent
  useEffect(() => {
    if (resetTrigger && resetTrigger > 0) {
      handleNewChat();
    }
  }, [resetTrigger]);

  // Helper function to format message content consistently
  const formatMessageContent = (content: string) => {
    let result = content
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>');

    // Handle mixed bullet/numbered lists by splitting on numbered items within text
    result = result
      // Split numbered items that appear mid-sentence (like "navigation). 2. Go to")
      .replace(/\)\.\s+(\d+\.\s+)/g, ').</p><p>$1')
      // Split numbered items that appear after periods (like "accuracy. 5. Select")
      .replace(/\.\s+(\d+\.\s+)/g, '.</p><p>$1')
      // Convert bullet points to list items
      .replace(/^[\s]*[-•]\s+(.+?)(?=\s+\d+\.|$)/gm, '<li>$1</li>')
      // Convert numbered items to list items
      .replace(/^[\s]*(\d+\.\s+)(.+?)(?=\s+\d+\.|$)/gm, '<li>$2</li>')
      // Convert remaining paragraphs
      .replace(/^(?!<li>|<\/li>|<ul>|<\/ul>)(.+)$/gm, '<p>$1</p>');

    // Wrap consecutive list items in ul tags
    result = result.replace(
      /(<li>.*?<\/li>(?:\s*<li>.*?<\/li>)*)/g,
      '<ul>$1</ul>',
    );

    // Clean up excessive whitespace and line breaks
    result = result.replace(/\s*\n\s*/g, '');

    // Special formatting for transcript content - support both new and legacy formats
    if (
      content.includes('📹 **Video:') ||
      content.includes('📄 **Transcript Preview:**') ||
      content.includes('## 📝 Transcript Preview')
    ) {
      result = result
        // Format video title with proper spacing
        .replace(
          /📹 \*\*Video:\s*(.*?)\*\*/g,
          '<div class="text-lg font-semibold text-blue-600 mb-4 flex items-center"><span class="mr-2">📹</span>Video: $1</div>',
        )
        // New format: ## 📝 Transcript Preview header with card styling
        .replace(
          /## 📝 Transcript Preview/g,
          '<div class="text-base font-semibold text-blue-600 my-5 p-3 bg-blue-50 dark:bg-blue-950/20 border-l-4 border-blue-600 rounded-lg flex items-center"><span class="mr-2">📝</span>Transcript Preview</div>',
        )
        // Format transcript preview header with spacing
        .replace(
          /📄 \*\*Transcript Preview:\*\*/g,
          '<div class="text-base font-semibold text-gray-700 dark:text-gray-300 my-5 flex items-center"><span class="mr-2">📄</span>Transcript Preview:</div>',
        )
        // Format timestamp entries with enhanced styling (supports MM:SS and HH:MM:SS)
        .replace(
          /\[(\d{1,2}:\d{2}(?::\d{2})?)\]\s*(.*?)(?=\[|\n|$)/g,
          '<div class="mb-4 p-3 bg-gray-50 dark:bg-gray-900 rounded-lg border-l-4 border-blue-600"><div class="text-xs text-blue-600 font-medium mb-1.5 font-mono bg-blue-100 dark:bg-blue-900/50 px-1.5 py-0.5 rounded inline-block">[$1]</div><div class="leading-relaxed text-gray-800 dark:text-gray-200">$2</div></div>',
        )
        // Format Read more link with styling
        .replace(
          /\[Read more\.\.\.\]/g,
          '<div class="my-5 p-3 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-950/20 dark:to-purple-950/20 rounded-lg border border-gray-200 dark:border-gray-700"><div class="font-semibold text-blue-600 mb-1">📖 Read More</div><div class="text-sm text-gray-600 dark:text-gray-400">Full transcript available in your video library</div></div>',
        )
        // Handle ellipsis for truncated content
        .replace(
          /(\.{3}\s*$)/,
          '<span class="text-gray-500 italic text-sm">... <em>Read more in full video analysis</em></span>',
        )
        // Handle remaining line breaks for non-transcript content
        .replace(/\n{3,}/g, '\n\n')
        .replace(/\n\n/g, '<div class="mb-3"></div>')
        .replace(/\n/g, '<br />');
    } else {
      // Standard formatting for non-transcript content
      result = result
        // Handle numbered steps
        .replace(/(\d+)\.\s+\*\*([^*]+)\*\*:?\s*/g, (match, num, title) => {
          return `<div class="my-5 p-4 bg-blue-50/50 dark:bg-blue-950/10 border-l-4 border-blue-600 rounded-lg">
            <div class="font-bold text-blue-600 text-base mb-3 flex items-center">
              <span class="bg-blue-600 text-white w-7 h-7 rounded-full flex items-center justify-center text-sm mr-3">${num}</span>
              <span>${title}</span>
            </div>
            <div class="leading-relaxed text-gray-700 dark:text-gray-300 ml-10">`;
        })
        // Close the step content divs
        .replace(
          /(<div class="leading-relaxed text-gray-700 dark:text-gray-300 ml-10">)(.*?)(?=<div class="my-5 p-4 bg-blue-50|$)/g,
          (match, opening, stepContent) => {
            return `${opening}${stepContent.trim()}</div></div>`;
          },
        )
        // Handle double line breaks for section spacing
        .replace(/\n\n/g, '<div class="mb-4"></div>')
        // Handle single line breaks
        .replace(/\n/g, '<br />');
    }

    return result;
  };

  useEffect(() => {
    scrollToBottom();
  }, [chatMessages]);

  // Helper function to parse user agent and extract browser details
  const parseBrowserInfo = () => {
    const {
      userAgent,
      platform,
      language,
      languages: navLanguages,
      appVersion,
    } = navigator;
    const languages = navLanguages?.join(', ') || language;

    // Enhanced browser detection with comprehensive logic
    const browserName = 'Unknown';
    const browserVersion = 'Unknown';
    const fullVersion = String(parseFloat(appVersion));
    const majorVersion = parseInt(appVersion, 10);

    // Browser detection logic (simplified for brevity)
    // ... (keeping the same browser detection logic from original)

    return {
      name: browserName,
      version: browserVersion,
      fullVersion,
      majorVersion,
      fullUserAgent: userAgent,
      language,
      languages,
      platform,
      operatingSystem: platform,
      vendor: navigator.vendor || 'Unknown',
      cookieEnabled: navigator.cookieEnabled,
      onLine: navigator.onLine,
    };
  };

  // Helper function to detect device type
  const getDeviceInfo = () => {
    const { userAgent, platform, maxTouchPoints = 0 } = navigator;

    let deviceType = 'desktop';

    // Mobile detection
    if (
      /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent)
    ) {
      deviceType = 'mobile';
    }
    // Tablet detection
    else if (
      /iPad|Android.*Tablet|Windows.*Touch/i.test(userAgent) ||
      maxTouchPoints > 2
    ) {
      deviceType = 'tablet';
    }
    // Desktop detection based on platform
    else if (/Win|Mac|Linux|X11/i.test(platform)) {
      deviceType = 'desktop';
    }

    return {
      type: deviceType,
      maxTouchPoints,
      screenWidth: window.screen.width,
      screenHeight: window.screen.height,
      viewportWidth: window.innerWidth,
      viewportHeight: window.innerHeight,
      pixelRatio: window.devicePixelRatio || 1,
    };
  };

  // Helper function to generate session information
  const getSessionInfo = () => {
    // Generate or get tab ID
    let tabId = sessionStorage.getItem('screenapp-tab-id');
    if (!tabId) {
      tabId = `tab_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      sessionStorage.setItem('screenapp-tab-id', tabId);
    }

    return {
      sessionStart:
        sessionStorage.getItem('screenapp-session-start') ||
        new Date().toISOString(),
      tabId,
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      timestamp: new Date().toISOString(),
      referrer: document.referrer || 'direct',
      origin: window.location.origin,
    };
  };

  // Helper function to collect relevant localStorage data (non-sensitive)
  const getRelevantLocalStorageData = () => {
    const relevantKeys = [
      'screenapp-theme',
      'screenapp-language',
      'screenapp-last-used-team',
      'screenapp-user-preferences',
      'screenapp-feature-flags',
    ];

    const data: Record<string, any> = {};
    relevantKeys.forEach((key) => {
      const value = localStorage.getItem(key);
      if (value) {
        try {
          data[key] = JSON.parse(value);
        } catch {
          data[key] = value;
        }
      }
    });

    return data;
  };

  const getCurrentUrlContext = () => {
    const currentUrl = window.location.href;
    const urlParams = new URLSearchParams(window.location.search);
    const hashPath = window.location.hash.replace('#', '');
    const pathParts = hashPath.split('/').filter(Boolean);
    const regularPathParts = window.location.pathname
      .split('/')
      .filter(Boolean);

    const context: any = {
      currentPage: currentUrl,
      path: hashPath || window.location.pathname,
      hostname: window.location.hostname,
      protocol: window.location.protocol,
      port: window.location.port,
      referrer: document.referrer,
      title: document.title,
      hash: window.location.hash,
      search: window.location.search,
    };

    const allPathParts = pathParts.length > 0 ? pathParts : regularPathParts;

    if (allPathParts.includes('library') && allPathParts.length >= 4) {
      const libraryIndex = allPathParts.indexOf('library');
      if (allPathParts[libraryIndex + 1]) {
        context.teamId = allPathParts[libraryIndex + 1];
      }
      if (allPathParts[libraryIndex + 3]) {
        context.fileId = allPathParts[libraryIndex + 3];
      }
    }

    if (allPathParts.includes('files') && allPathParts.length > 1) {
      const fileIndex = allPathParts.indexOf('files');
      if (allPathParts[fileIndex + 1]) {
        context.fileId = allPathParts[fileIndex + 1];
      }
    }

    if (
      !context.teamId &&
      allPathParts.includes('team') &&
      allPathParts.length > 1
    ) {
      const teamIndex = allPathParts.indexOf('team');
      if (allPathParts[teamIndex + 1]) {
        context.teamId = allPathParts[teamIndex + 1];
      }
    }

    // Add section detection
    if (allPathParts.includes('dashboard')) {
      context.section = 'dashboard';
    } else if (allPathParts.includes('library')) {
      context.section = 'library';
    } else if (allPathParts.includes('teams')) {
      context.section = 'teams';
    } else if (allPathParts.includes('settings')) {
      context.section = 'settings';
    } else if (allPathParts.includes('billing')) {
      context.section = 'billing';
    }

    if (urlParams.toString()) {
      context.urlParams = Object.fromEntries(urlParams);
    }

    return context;
  };

  const buildChatContext = (): ChatContext => {
    const getPlanLimits = (packageType?: string) => {
      switch (packageType) {
        case 'FREE':
          return { uploads: 3, transcriptions: 0, aiQuestions: 0 };
        case 'FREE_LOGGEDIN':
          return { uploads: 3, transcriptions: 1, aiQuestions: 3 };
        case 'GROWTH':
          return { uploads: 50, transcriptions: 50, aiQuestions: 50 };
        case 'BUSINESS':
          return {
            uploads: 'unlimited',
            transcriptions: 'unlimited',
            aiQuestions: 999,
          };
        case 'ENTERPRISE':
          return {
            uploads: 'unlimited',
            transcriptions: 'unlimited',
            aiQuestions: 'unlimited',
          };
        default:
          return { uploads: 0, transcriptions: 0, aiQuestions: 0 };
      }
    };

    const planLimits = getPlanLimits(subscription?.package);
    const currentUsage = usage || {};

    // Collect comprehensive context information
    const browserInfo = parseBrowserInfo();
    const deviceInfo = getDeviceInfo();
    const sessionInfo = getSessionInfo();
    const localStorage = getRelevantLocalStorageData();
    const urlContext = getCurrentUrlContext();

    return {
      userName: user?.name,
      userEmail: user?.email,
      subscription: subscription
        ? {
            package: subscription.package,
            term: subscription.term,
            price: subscription.price,
            renewalDate: subscription.currentPeriodStartedAt
              ? new Date(
                  new Date(subscription.currentPeriodStartedAt).getTime() +
                    (subscription.term === 'annual'
                      ? 365 * 24 * 60 * 60 * 1000
                      : 30 * 24 * 60 * 60 * 1000),
                ).toLocaleDateString()
              : undefined,
            currentPeriodStartedAt: subscription.currentPeriodStartedAt,
          }
        : undefined,
      usage: usage
        ? {
            uploadCount: currentUsage.uploadCount || 0,
            uploadLimit: planLimits.uploads,
            uploadRemaining:
              typeof planLimits.uploads === 'number'
                ? Math.max(
                    0,
                    planLimits.uploads - (currentUsage.uploadCount || 0),
                  )
                : 'unlimited',
            askAICount: currentUsage.askAICount || 0,
            askAILimit: planLimits.aiQuestions,
            askAIRemaining:
              typeof planLimits.aiQuestions === 'number'
                ? Math.max(
                    0,
                    planLimits.aiQuestions - (currentUsage.askAICount || 0),
                  )
                : 'unlimited',
            transcriptionCount: currentUsage.transcriptionCount || 0,
            transcriptionLimit: planLimits.transcriptions,
            transcriptionRemaining:
              typeof planLimits.transcriptions === 'number'
                ? Math.max(
                    0,
                    planLimits.transcriptions -
                      (currentUsage.transcriptionCount || 0),
                  )
                : 'unlimited',
            videoAskAICount: currentUsage.videoAskAICount || 0,
          }
        : undefined,
      team: selectedTeam
        ? {
            name: selectedTeam.name,
            membersCount: selectedTeam.teamSpaceMembers?.length,
            teamId: selectedTeam.teamId,
            ownerEmail: selectedTeam.email,
            role: selectedTeam.email === user?.email ? 'Owner' : 'Member',
          }
        : undefined,
      urlContext,
      browserInfo,
      deviceInfo,
      sessionInfo,
      localStorage,
    };
  };

  const handleChatSend = async (message?: string) => {
    const messageToSend = message || chatInput.trim();
    if (!messageToSend || isStreaming) return;

    const userMessage: ChatMessage = {
      role: 'user',
      content: messageToSend,
      timestamp: new Date(),
      id: generateMessageId(),
    };

    setChatMessages((prev) => [...prev, userMessage]);
    setChatInput('');
    setIsStreaming(true);
    setChatError(null);

    // Add a placeholder message for streaming
    const streamingMessage: ChatMessage = {
      role: 'assistant',
      content: '',
      timestamp: new Date(),
      id: generateMessageId(),
    };
    setChatMessages((prev) => [...prev, streamingMessage]);

    try {
      const context = buildChatContext();
      const conversationHistory = chatMessages.map((msg) => ({
        role: msg.role,
        content: msg.content,
      }));

      const response = await sendChatMessageWithActionsStreaming(
        messageToSend,
        context,
        conversationHistory,
        currentSessionId || undefined,
        (token: string) => {
          // Update the streaming message in real-time
          setChatMessages((prev) =>
            prev.map((msg, index) =>
              index === prev.length - 1
                ? { ...msg, content: msg.content + token }
                : msg,
            ),
          );
        },
      );

      if (response.sessionId && !currentSessionId) {
        setCurrentSessionId(response.sessionId);
      }

      // Update the final message with complete data (preserve streamed content)
      setChatMessages((prev) =>
        prev.map((msg, index) =>
          index === prev.length - 1
            ? {
                ...msg,
                // Keep the streamed content, don't replace it
                toolCalls: response.toolCalls,
                actionButtons: response.actionButtons || [],
                escalated: response.escalated,
              }
            : msg,
        ),
      );
    } catch (error) {
      console.error('Error sending message:', error);
      const errorMessage =
        error instanceof Error ? error.message : 'An unexpected error occurred';
      setChatError(errorMessage);

      // Check if this is a system error that should trigger automatic escalation
      const isSystemError =
        errorMessage.includes('validation failed') ||
        errorMessage.includes('Cannot populate path') ||
        errorMessage.includes('JSON input') ||
        errorMessage.includes('Path `content` is required') ||
        errorMessage.includes('StrictPopulateError') ||
        errorMessage.includes('Unexpected end of JSON input') ||
        errorMessage.includes('escalateImmediately');

      let errorContent =
        '🚨 I encountered a technical issue. Our support team has been automatically notified and will contact you directly at your email address within 24 hours to resolve this issue.\n\n⚠️ Please check your email inbox (and spam folder) for our response.\n\n📧 You can also contact us directly at support@screenapp.io if you need immediate assistance.';

      if (isSystemError) {
        errorContent =
          "🚨 I encountered a technical issue on our end. I've automatically escalated this to our support team with all the technical details.\n\n📧 **Our support team will contact you directly at your email address within 24 hours** to resolve this issue.\n\n⚠️ Please check your email inbox (and spam folder) for our response.\n\n📧 You can also contact us directly at support@screenapp.io if you need immediate assistance.";

        // Set toast to inform about automatic escalation
        toast('Support team automatically notified');
      }

      // Replace the streaming message with error message
      setChatMessages((prev) =>
        prev.map((msg, index) =>
          index === prev.length - 1
            ? {
                ...msg,
                content: errorContent,
              }
            : msg,
        ),
      );
    } finally {
      setIsStreaming(false);
    }
  };

  const handleQuickAction = async (chipData: any) => {
    try {
      // Send PostHog event for quick action click
      posthog.capture('support-quick-action-clicked', {
        action_title: chipData.title,
        action_label: chipData.label,
        action_icon: chipData.icon,
        action_color: chipData.color,
        predefined_question: chipData.predefinedQuestion,
      });

      setIsStreaming(true);
      setChatError(null);

      const userMessage: ChatMessage = {
        role: 'user',
        content: chipData.title,
        timestamp: new Date(),
        id: generateMessageId(),
      };
      setChatMessages((prev) => [...prev, userMessage]);

      // Use multi-agent system for all quick actions
      const streamingMessage: ChatMessage = {
        role: 'assistant',
        content: '',
        timestamp: new Date(),
        id: generateMessageId(),
      };
      setChatMessages((prev) => [...prev, streamingMessage]);

      const context = buildChatContext();
      const conversationHistory = chatMessages.map((msg) => ({
        role: msg.role,
        content: msg.content,
      }));

      // Force specific agent based on quick action
      let forceAgent: string;
      switch (chipData.title) {
        case 'Help with recording':
          forceAgent = 'technical';
          break;
        case 'Improve my transcript':
          forceAgent = 'technical';
          break;
        case 'Account and Billing':
        case 'Cancel Subscription':
        case 'Accidental Charge':
        case 'Monthly Plan Options':
        case 'Check Usage & Limits':
          forceAgent = 'billing';
          break;
        case 'Discover AI Features':
          forceAgent = 'ai-features';
          break;
        default:
          forceAgent = 'general';
      }

      const response = await sendChatMessageWithActionsStreaming(
        chipData.predefinedQuestion,
        context,
        conversationHistory,
        currentSessionId || undefined,
        (token: string) => {
          // Update the streaming message in real-time
          setChatMessages((prev) =>
            prev.map((msg, index) =>
              index === prev.length - 1
                ? { ...msg, content: msg.content + token }
                : msg,
            ),
          );
        },
        { useMultiAgent: true, forceAgent },
      );

      if (response.sessionId && !currentSessionId) {
        setCurrentSessionId(response.sessionId);
      }

      // Update the final message with complete data (preserve streamed content)
      setChatMessages((prev) =>
        prev.map((msg, index) =>
          index === prev.length - 1
            ? {
                ...msg,
                // Keep the streamed content, don't replace it
                toolCalls: response.toolCalls,
                actionButtons: response.actionButtons || [],
                escalated: response.escalated,
              }
            : msg,
        ),
      );
    } catch (error) {
      console.error('Error handling quick action:', error);
      setChatError('Failed to load assistance. Please try again.');
    } finally {
      setIsStreaming(false);
    }
  };

  const handleChatKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleChatSend();
    }
  };

  // Handle feedback submission
  const handleFeedback = async (
    messageId: string,
    feedbackType: 'like' | 'dislike',
  ) => {
    if (!currentSessionId) {
      toast.error('Cannot submit feedback without an active session.');
      return;
    }

    // Check if the same feedback is already selected - if so, remove it
    const currentFeedback =
      messageFeedback[messageId]?.type ||
      chatMessages.find(
        (msg) =>
          msg.id === messageId ||
          (messageId === 'test-id' && msg.role === 'assistant'),
      )?.feedback?.type;

    if (currentFeedback === feedbackType) {
      // Remove feedback
      setMessageFeedback((prev) => {
        const newState = { ...prev };
        delete newState[messageId];
        return newState;
      });

      // Remove feedback from message
      setChatMessages((prev) =>
        prev.map((msg) =>
          msg.id === messageId ||
          (messageId === 'test-id' && msg.role === 'assistant')
            ? {
                ...msg,
                feedback: undefined,
              }
            : msg,
        ),
      );
      return;
    }

    // Immediately update visual state for instant feedback
    setMessageFeedback((prev) => ({
      ...prev,
      [messageId]: { type: feedbackType, loading: false },
    }));

    // Update the message in chatMessages to include feedback immediately
    setChatMessages((prev) =>
      prev.map((msg) =>
        msg.id === messageId ||
        (messageId === 'test-id' && msg.role === 'assistant')
          ? {
              ...msg,
              feedback: {
                type: feedbackType,
                timestamp: new Date(),
              },
            }
          : msg,
      ),
    );

    try {
      const result = await sendChatFeedback(
        messageId,
        currentSessionId,
        feedbackType,
      );

      if (!result.success) {
        // Only show error messages, remove visual feedback on failure
        setMessageFeedback((prev) => {
          const newState = { ...prev };
          delete newState[messageId];
          return newState;
        });

        // Remove feedback from message on failure
        setChatMessages((prev) =>
          prev.map((msg) =>
            msg.id === messageId ||
            (messageId === 'test-id' && msg.role === 'assistant')
              ? {
                  ...msg,
                  feedback: undefined,
                }
              : msg,
          ),
        );

        toast.error(result.message || 'Failed to submit feedback.');
      }
      // No success message - just keep the visual feedback
    } catch {
      // Remove visual feedback on error
      setMessageFeedback((prev) => {
        const newState = { ...prev };
        delete newState[messageId];
        return newState;
      });

      // Remove feedback from message on error
      setChatMessages((prev) =>
        prev.map((msg) =>
          msg.id === messageId ||
          (messageId === 'test-id' && msg.role === 'assistant')
            ? {
                ...msg,
                feedback: undefined,
              }
            : msg,
        ),
      );

      toast.error('Failed to submit feedback. Please try again.');
    }
  };

  const handleActionButtonClick = async (button: ActionButton) => {
    try {
      setIsActionButtonLoading(button.id);
      const context = buildChatContext();

      const userActionMessage: ChatMessage = {
        role: 'user',
        content: `${button.label}`,
        timestamp: new Date(),
        id: generateMessageId(),
      };
      setChatMessages((prev) => [...prev, userActionMessage]);

      const result = await handleButtonAction(button, context);

      if (result.success) {
        if (result.data?.logout) {
          // Handle logout for cache clearing
          const { delay = 3000 } = result.data;

          toast(
            result.message || 'Cache cleared successfully. Refreshing page...',
          );

          const assistantMessage: ChatMessage = {
            role: 'assistant',
            content: result.message || 'Cache cleared successfully.',
            timestamp: new Date(),
            id: generateMessageId(),
          };
          setChatMessages((prev) => [...prev, assistantMessage]);

          // Logout and refresh page after delay
          setTimeout(() => {
            // Clear any remaining auth tokens
            localStorage.removeItem('token');
            sessionStorage.removeItem('token');
            // Refresh the page to complete the cache clearing process
            window.location.reload();
          }, delay);
        } else if (result.data?.redirect) {
          const { redirect, openInNewTab } = result.data;
          if (openInNewTab) {
            window.open(redirect, '_blank');
          } else {
            navigate(redirect);
          }
          toast(result.message || 'Redirecting...');
        } else {
          toast(result.message || 'Action completed successfully.');
        }

        // Only add assistant message if not logging out (to avoid duplicate messages)
        if (!result.data?.logout) {
          const assistantMessage: ChatMessage = {
            role: 'assistant',
            content: result.message || 'Action completed successfully.',
            timestamp: new Date(),
            id: generateMessageId(),
          };
          setChatMessages((prev) => [...prev, assistantMessage]);
        }
      } else {
        toast.error(result.message || 'Action failed.');

        const assistantMessage: ChatMessage = {
          role: 'assistant',
          content: result.message || 'Action failed. Please try again.',
          timestamp: new Date(),
          id: generateMessageId(),
        };
        setChatMessages((prev) => [...prev, assistantMessage]);
      }
    } catch (error) {
      console.error('Error handling button action:', error);

      toast.error('An error occurred while processing the action.');

      const assistantMessage: ChatMessage = {
        role: 'assistant',
        content:
          'An error occurred while processing the action. Please try again.',
        timestamp: new Date(),
        id: generateMessageId(),
      };
      setChatMessages((prev) => [...prev, assistantMessage]);
    } finally {
      setIsActionButtonLoading(null);
    }
  };

  return (
    <div className="h-full flex flex-col">
      {/* Messages area - takes up all available space */}
      <div className="flex-1 min-h-0 flex flex-col">
        {/* Show loading state while loading history */}
        {isLoadingHistory && (
          <div className="flex-1 flex items-center justify-center">
            <div className="flex flex-col items-center gap-4 text-center">
              <Loader className="h-8 w-8 animate-spin text-muted-foreground" />
              <p className="text-sm text-muted-foreground">
                Loading your conversation...
              </p>
            </div>
          </div>
        )}

        {/* Show overview only if not loading and no messages */}
        {!isLoadingHistory && chatMessages.length === 0 && (
          <div className="flex-1 flex flex-col items-center justify-start pt-12">
            <Overview />
          </div>
        )}

        {/* Messages container */}
        {chatMessages.length > 0 && (
          <div
            ref={messagesContainerRef}
            className="flex-1 min-h-0 w-full overflow-y-auto flex flex-col px-6 pr-12 pt-6 pb-4 scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-700"
          >
            {/* Spacer to push content to bottom */}
            <div className="flex-1 min-h-0" />
            {chatMessages.map((message, index) => (
              <div
                key={index}
                className={cn(
                  'flex gap-3 animate-in fade-in slide-in-from-bottom-2 duration-300',
                  message.role === 'user' ? 'flex-row-reverse' : 'flex-row',
                  'justify-start',
                  index === 0 && 'pt-4',
                  index > 0 && 'mt-4',
                )}
              >
                {/* Avatar */}
                {message.role === 'assistant' ? (
                  <img
                    src={botIcon}
                    alt="ScreenApp"
                    className="w-6 h-6 object-contain flex-shrink-0"
                  />
                ) : (
                  <>
                    {user?.picture ? (
                      <img
                        src={user.picture}
                        alt={user?.name || 'User'}
                        className="w-6 h-6 object-cover rounded flex-shrink-0"
                      />
                    ) : (
                      <div className="w-6 h-6 rounded bg-muted flex items-center justify-center flex-shrink-0">
                        <User className="w-3.5 h-3.5 text-muted-foreground" />
                      </div>
                    )}
                  </>
                )}

                {/* Message content */}
                <div className="min-w-0 max-w-[85%]">
                  {/* Show thinking animation if assistant message is empty (waiting for streaming to start) */}
                  {message.role === 'assistant' &&
                  message.content === '' &&
                  isStreaming ? (
                    <div className="pt-2 pb-4 pl-4">
                      <div className="flex gap-1">
                        <span className="w-2 h-2 bg-gray-400 rounded-full" />
                        <span className="w-2 h-2 bg-gray-400 rounded-full" />
                        <span className="w-2 h-2 bg-gray-400 rounded-full" />
                      </div>
                    </div>
                  ) : (
                    <div
                      className={cn(
                        'prose prose-xs dark:prose-invert max-w-none',
                        '[&>*]:text-sm [&>p]:text-sm [&>ul]:text-sm [&>ol]:text-sm [&>li]:text-sm',
                        message.role === 'user' &&
                          'bg-muted rounded-2xl rounded-tr-none px-4 py-3',
                        message.role === 'assistant' && 'pr-[10%]',
                      )}
                      // eslint-disable-next-line react/no-danger
                      dangerouslySetInnerHTML={{
                        __html: formatMessageContent(message.content),
                      }}
                    />
                  )}

                  {/* Action buttons */}
                  {message.actionButtons &&
                    message.actionButtons.length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-2 ml-4">
                        {message.actionButtons.map((button: ActionButton) => (
                          <DynamicActionButton
                            key={button.id}
                            button={button}
                            isLoading={isActionButtonLoading === button.id}
                            onClick={handleActionButtonClick}
                          />
                        ))}
                      </div>
                    )}

                  {/* Feedback buttons for assistant messages */}
                  {message.role === 'assistant' && message.content && (
                    <div className="mt-2 flex items-center gap-1 justify-end pr-[10%]">
                      <Button
                        variant="ghost"
                        size="icon"
                        className={cn(
                          'h-6 w-6 rounded-full',
                          (message.feedback?.type === 'like' ||
                            messageFeedback[message.id || 'test-id']?.type ===
                              'like') &&
                            'text-blue-600',
                        )}
                        onClick={() =>
                          handleFeedback(message.id || 'test-id', 'like')
                        }
                        disabled={
                          messageFeedback[message.id || 'test-id']?.loading
                        }
                      >
                        {messageFeedback[message.id || 'test-id']?.loading &&
                        messageFeedback[message.id || 'test-id']?.type ===
                          'like' ? (
                          <Loader className="h-3 w-3" />
                        ) : (
                          <ThumbsUp className="h-3 w-3" />
                        )}
                      </Button>

                      <Button
                        variant="ghost"
                        size="icon"
                        className={cn(
                          'h-6 w-6 rounded-full',
                          (message.feedback?.type === 'dislike' ||
                            messageFeedback[message.id || 'test-id']?.type ===
                              'dislike') &&
                            'text-red-500',
                        )}
                        onClick={() =>
                          handleFeedback(message.id || 'test-id', 'dislike')
                        }
                        disabled={
                          messageFeedback[message.id || 'test-id']?.loading
                        }
                      >
                        {messageFeedback[message.id || 'test-id']?.loading &&
                        messageFeedback[message.id || 'test-id']?.type ===
                          'dislike' ? (
                          <Loader className="h-3 w-3" />
                        ) : (
                          <ThumbsDown className="h-3 w-3" />
                        )}
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            ))}

            {/* Error message */}
            {chatError && (
              <Alert variant="destructive" className="max-w-lg mx-auto">
                <AlertDescription>{chatError}</AlertDescription>
              </Alert>
            )}

            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      {/* Quick action chips - shown when no messages */}
      {!isLoadingHistory && chatMessages.length === 0 && (
        <div className="w-full px-6 pb-4">
          <div className="grid gap-3 sm:grid-cols-2 mx-auto max-w-4xl">
            {quickActionChips.map((chip, index) => (
              <Card
                key={index}
                className={cn(
                  'px-4 py-3 cursor-pointer transition-colors duration-150',
                  'hover:bg-muted/50 border-muted bg-card',
                  'flex flex-col justify-center items-start',
                )}
                onClick={() => handleQuickAction(chip)}
              >
                <h3 className="text-sm font-medium text-foreground/85 mb-1">
                  {chip.title}
                </h3>
                <p className="text-xs text-muted-foreground">{chip.label}</p>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Input area - always at bottom */}
      <div className="w-full px-6 pb-6 pt-4">
        <div className="max-w-4xl mx-auto relative">
          <Textarea
            ref={textareaRef}
            className="min-h-[40px] h-[40px] resize-none py-2 pr-12 rounded-full text-sm"
            placeholder={
              chatMessages.length > 0
                ? 'How can I help?'
                : 'Ask me anything about ScreenApp...'
            }
            value={chatInput}
            onChange={(e) => setChatInput(e.target.value)}
            onKeyDown={handleChatKeyPress}
            rows={1}
          />
          <Button
            size="icon"
            onClick={() => handleChatSend()}
            disabled={!chatInput.trim() || isStreaming}
            className="absolute right-1 top-1/2 -translate-y-1/2 h-8 w-8 rounded-full"
          >
            <ArrowUp className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AIAssistantForm;
